import React from 'react';
import Singletons from 'controllers/Singletons';
import api from 'controllers/raymanAPI';
import {FilesCtx, FilesCtxProvider} from './files';

function mapOperation(item)
{
    let currentUserRole = "admin";
    if(item.profiles.experts === Singletons.currentUser.userID) currentUserRole = "expert";
    if(item.profiles.operators === Singletons.currentUser.userID) currentUserRole = "operator";
    
    return {...item, id: item.operationID, notifications: currentUserRole !== "admin" ? item.unreadMessages : 0, operator: item.profiles.operators, expert: item.profiles.experts, closed: item.disabled, currentUserRole: currentUserRole}
}

async function getData()
{
    let indexed = {};

    let response = await api.operations.getAll(Singletons.currentUser.profiles);
    let data = await response.json();

    data = data.map((item)=>mapOperation(item));
    data.forEach(item => indexed[item.operationID] = item);
    
    return {array: data, indexed: indexed};
}

const OperationsCtx = React.createContext({
    loading: true, 
    data: {
        array: [], 
        indexed: {}
    }, 
    funcs: {
        clearNotifications: (opID) => {},
        create: ()=>{},
        disable: (opID)=>{},
        enable: (opID)=>{},
        update: (opID, data)=>{}
    }
});

function OperationsCtxProvider(props)
{

    const [loading, setLoading] = React.useState(true);
    const [data, setData] = React.useState({array: [], indexed: []});

    React.useEffect(() => {
        getData().then(values => {
            setData(values);
            setLoading(false);
        });
    }, []);

    const internalAdd = React.useCallback(async (opID, opData) => {
        // ignore duplicated messages
        if(data.indexed[opID] !== undefined) return;
        
        let tmp = {...data};
        if(!opData)
        {
            let result = await api.operations.getSingle(opID);
            if(result.ok) opData = await result.json();
        }
        if(opData)
        {
            let item = mapOperation(opData);
            let index = tmp.array.push(item);
            tmp.indexed[item.id] = tmp.array[index-1];
            
            setData(tmp);
        }
    }, [data]);

    const internalUpdate = React.useCallback((opID, opData) => {
        let tmp = {...data};
        Object.keys(opData).forEach((key) => {
            tmp.indexed[opID][key] = opData[key];
        })
        // tmp.indexed[opID] = {...tmp.indexed[opID], ...opData};
        setData(tmp);
    }, [data]);

    const internalUpdateFromAPI = React.useCallback(async (opID) => {
        let result = await api.operations.getSingle(opID);
        if(result.ok)
        {
            let item = mapOperation(await result.json());
            internalUpdate(item.id, item);            
        }
    }, [internalUpdate]);

    // const internalDelete = React.useCallback((opID, data) => {

    // }, [data]);


    //#region Notification Handlers

    React.useEffect( function handleChatNotifications() {
        function notificationHandler(notification, target) {
            if(target !== 'user') return;

            let tmp = {...data};
            let opID = notification.operationID;
            if(tmp.indexed[opID])
            {
                tmp.indexed[opID].notifications += 1;
                tmp.indexed[opID].lastMessage = notification.datetime;
                setData(tmp);
            }
        }

        Singletons.NotificationsSocket.on('operations.chat.messages.created', notificationHandler);
        return () => {Singletons.NotificationsSocket.off('operations.chat.messages.created', notificationHandler);}
    }, [data]);

    React.useEffect( function handleOperationsListChangedNotification() {

        const create = (message) => internalAdd(message.operationID, message);
        const enable = (message) => internalUpdate(message.operationID, {closed: false, closeDatetime: null});
        const disable = (message) => internalUpdate(message.operationID, {closed: true, closeDatetime: message.datetime});
        const update = (message) => internalUpdate(message.operationID, message);

        Singletons.NotificationsSocket.on('operations.created', create);
        Singletons.NotificationsSocket.on('operations.disabled', disable);
        Singletons.NotificationsSocket.on('operations.enabled', enable);
        Singletons.NotificationsSocket.on('operations.updated', update);

        return () => {
            Singletons.NotificationsSocket.off('operations.created', create);
            Singletons.NotificationsSocket.off('operations.disabled', disable);
            Singletons.NotificationsSocket.off('operations.enabled', enable);
            Singletons.NotificationsSocket.off('operations.updated', update);
        }

    }, [internalAdd, internalUpdate]);

    //#endregion


    const clearNotifications = React.useCallback((opID) => {
        let tmp = {...data};
        
        if(tmp.indexed[opID] && tmp.indexed[opID].notifications && tmp.indexed[opID].notifications > 0)
        {
            tmp.indexed[opID].notifications = 0;
            setData(tmp);
        }
    }, [data]);


    const create = React.useCallback(async (data) => {
        let result = await api.operations.post(data);
        if(result.ok)
        {
            //internalAdd(await result.json().operationID);
            // Se recibe como notificación, no hace falta añadirlo a mano
        }
    }, []);

    const update = React.useCallback(async (opID, data) => {
        let result = await api.operations.updateSingle(opID, data);
        if(result.ok) internalUpdate(opID, data);
    }, [internalUpdate]);

    const disable = React.useCallback(async (opID) => {
        let result = await api.operations.disableSingle(opID);
        if(result.ok) internalUpdateFromAPI(opID);
    }, [internalUpdateFromAPI]);

    const enable = React.useCallback(async (opID) => {
        let result = await api.operations.enableSingle(opID);
        if(result.ok) internalUpdateFromAPI(opID);
    }, [internalUpdateFromAPI]);

    return <OperationsCtx.Provider value={{loading: loading, data: {...data}, funcs: {clearNotifications: clearNotifications, create: create, update: update, disable: disable, enable: enable}}} >
        {props.children}
        </OperationsCtx.Provider>
}

function OperationFilesProvider({operationID, ...props})
{
    return <FilesCtxProvider apiEndpoint={api.operations.files} apiParams={[operationID]}>
        <OperationFilesNotificationHandler operationID={operationID}>
            {props.children}
        </OperationFilesNotificationHandler>
        </FilesCtxProvider>
}

function OperationFilesNotificationHandler({operationID, ...props})
{
    const filesCtx = React.useContext(FilesCtx);

    React.useEffect( function handleChatNotifications() {

        const addFile = (message) => {if(message.operationID === operationID) filesCtx.funcs.internal.add(message.fileInfo.fileID, message.fileInfo);}
        const deleteFile = (message) => {if(message.operationID === operationID) filesCtx.funcs.internal.delete(message.fileID);}
        const refreshFile = (message) => {if(message.operationID === operationID) filesCtx.funcs.internal.refresh(message.fileInfo.fileID);}

        Singletons.NotificationsSocket.on('operations.files.created', addFile);
        Singletons.NotificationsSocket.on('operations.files.deleted', deleteFile);
        Singletons.NotificationsSocket.on('operations.files.updated', refreshFile);

        return () => {
            Singletons.NotificationsSocket.off('operations.files.created', addFile);
            Singletons.NotificationsSocket.off('operations.files.deleted', deleteFile);
            Singletons.NotificationsSocket.off('operations.files.updated', refreshFile);
        }
    }, [filesCtx, operationID]);

    return <>{props.children}</>;

}

export {OperationsCtx, OperationsCtxProvider, OperationFilesProvider};
export {FilesCtx as OperationFilesCtx} from './files';